'use client';

import React, { useState, useMemo } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { BookOpen, Search, Clock, TrendingUp, ChevronRight, Briefcase, Filter } from 'lucide-react';
import Link from 'next/link';

interface CourseItem {
  id: string;
  name: string;
  level: 'UG' | 'PG' | 'Diploma' | 'Doctorate';
  stream: string;
  duration: string;
  avgFees: string;
  topExam: string;
  avgSalary: string;
  demand: 'Very High' | 'High' | 'Medium';
  description: string;
  topColleges: string[];
  skills: string[];
}

const courses: CourseItem[] = [
  {
    id: 'c1', name: 'B.Tech Computer Science & Engineering', level: 'UG', stream: 'Engineering',
    duration: '4 Years', avgFees: '₹3L – ₹20L', topExam: 'JEE Main / Advanced', avgSalary: '₹6L – ₹40L',
    demand: 'Very High', description: 'Core programming, algorithms, AI/ML, software development and system design.',
    topColleges: ['IIT Delhi', 'IIT Bombay', 'BITS Pilani', 'NIT Trichy'],
    skills: ['Programming', 'Data Structures', 'AI/ML', 'Cloud Computing'],
  },
  {
    id: 'c2', name: 'MBBS', level: 'UG', stream: 'Medical',
    duration: '5.5 Years', avgFees: '₹50K – ₹80L', topExam: 'NEET UG', avgSalary: '₹8L – ₹30L',
    demand: 'Very High', description: 'Comprehensive medical education covering anatomy, physiology, clinical practice.',
    topColleges: ['AIIMS Delhi', 'Manipal University', 'CMC Vellore'],
    skills: ['Clinical Diagnosis', 'Patient Care', 'Surgery Basics', 'Pharmacology'],
  },
  {
    id: 'c3', name: 'MBA (Master of Business Administration)', level: 'PG', stream: 'Management',
    duration: '2 Years', avgFees: '₹5L – ₹25L', topExam: 'CAT / XAT / MAT', avgSalary: '₹8L – ₹80L',
    demand: 'Very High', description: 'Business strategy, finance, marketing, operations and leadership skills.',
    topColleges: ['IIM Ahmedabad', 'IIM Bangalore', 'IIM Calcutta'],
    skills: ['Leadership', 'Finance', 'Marketing', 'Strategy'],
  },
  {
    id: 'c4', name: 'BA LLB (Integrated Law)', level: 'UG', stream: 'Law',
    duration: '5 Years', avgFees: '₹1L – ₹13L', topExam: 'CLAT / AILET', avgSalary: '₹4L – ₹20L',
    demand: 'High', description: 'Integrated program combining arts and law for comprehensive legal education.',
    topColleges: ['NLSIU Bangalore', 'NLU Delhi', 'NALSAR Hyderabad'],
    skills: ['Legal Research', 'Drafting', 'Advocacy', 'Constitutional Law'],
  },
  {
    id: 'c5', name: 'B.Com (Honours)', level: 'UG', stream: 'Commerce',
    duration: '3 Years', avgFees: '₹30K – ₹3L', topExam: 'CUET', avgSalary: '₹3L – ₹12L',
    demand: 'High', description: 'Accounting, taxation, finance, economics and business law fundamentals.',
    topColleges: ['Delhi University', 'Shri Ram College', 'Christ University'],
    skills: ['Accounting', 'Taxation', 'Financial Analysis', 'Auditing'],
  },
  {
    id: 'c6', name: 'B.Tech Artificial Intelligence & ML', level: 'UG', stream: 'Engineering',
    duration: '4 Years', avgFees: '₹4L – ₹22L', topExam: 'JEE Main / VITEEE', avgSalary: '₹8L – ₹50L',
    demand: 'Very High', description: 'Machine learning, deep learning, NLP, computer vision and AI applications.',
    topColleges: ['VIT Vellore', 'Chandigarh University', 'Amity University'],
    skills: ['Python', 'TensorFlow', 'NLP', 'Computer Vision'],
  },
  {
    id: 'c7', name: 'M.Tech Computer Science', level: 'PG', stream: 'Engineering',
    duration: '2 Years', avgFees: '₹1L – ₹5L', topExam: 'GATE', avgSalary: '₹10L – ₹45L',
    demand: 'Very High', description: 'Advanced computing, research-oriented program in algorithms and systems.',
    topColleges: ['IIT Delhi', 'IIT Bombay', 'NIT Trichy'],
    skills: ['Advanced Algorithms', 'Research', 'System Design', 'Networking'],
  },
  {
    id: 'c8', name: 'B.A. Economics (Honours)', level: 'UG', stream: 'Arts',
    duration: '3 Years', avgFees: '₹20K – ₹2L', topExam: 'CUET', avgSalary: '₹3L – ₹15L',
    demand: 'Medium', description: 'Micro/macroeconomics, econometrics, public policy and development economics.',
    topColleges: ['Delhi University', 'Jadavpur University', 'Presidency University'],
    skills: ['Economic Analysis', 'Statistics', 'Policy Research', 'Econometrics'],
  },
];

const levels = ['All', 'UG', 'PG', 'Diploma', 'Doctorate'];
const streams = ['All', 'Engineering', 'Medical', 'Management', 'Law', 'Commerce', 'Arts'];
const demandColors: Record<string, string> = {
  'Very High': 'bg-green-100 text-green-700',
  'High': 'bg-blue-100 text-blue-700',
  'Medium': 'bg-amber-100 text-amber-700',
};

export default function CourseFinderPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('All');
  const [selectedStream, setSelectedStream] = useState('All');
  const [selectedCourse, setSelectedCourse] = useState<CourseItem | null>(null);

  const filtered = useMemo(() => {
    return courses.filter((c) => {
      const matchSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.stream.toLowerCase().includes(searchQuery.toLowerCase());
      const matchLevel = selectedLevel === 'All' || c.level === selectedLevel;
      const matchStream = selectedStream === 'All' || c.stream === selectedStream;
      return matchSearch && matchLevel && matchStream;
    });
  }, [searchQuery, selectedLevel, selectedStream]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <div className="bg-secondary text-white py-10 px-4 lg:px-8 xl:px-10 2xl:px-16">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-2 text-slate-300 text-sm mb-3">
              <Link href="/home" className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span>Course Finder</span>
            </div>
            <h1 className="text-3xl font-extrabold text-white mb-2">Course Finder</h1>
            <p className="text-slate-300 text-sm max-w-xl">
              Explore courses across streams, compare fees, career prospects, and find the right path for you.
            </p>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">
          {/* Filters */}
          <div className="bg-card rounded-2xl border border-border p-5 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={15} />
                <input
                  type="text"
                  placeholder="Search courses, streams..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-base pl-9 text-sm"
                />
              </div>
              <div className="relative sm:w-40">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                <select
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                  className="select-base pl-9 text-sm appearance-none cursor-pointer"
                >
                  {levels.map((l) => <option key={l} value={l}>{l === 'All' ? 'All Levels' : l}</option>)}
                </select>
              </div>
              <div className="relative sm:w-44">
                <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                <select
                  value={selectedStream}
                  onChange={(e) => setSelectedStream(e.target.value)}
                  className="select-base pl-9 text-sm appearance-none cursor-pointer"
                >
                  {streams.map((s) => <option key={s} value={s}>{s === 'All' ? 'All Streams' : s}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Course list */}
            <div className="flex-1 min-w-0 space-y-4">
              <p className="text-sm text-muted-foreground font-semibold">
                <span className="text-primary font-bold">{filtered.length}</span> courses found
              </p>
              {filtered.map((course) => (
                <div
                  key={course.id}
                  onClick={() => setSelectedCourse(course)}
                  className={`bg-card rounded-2xl border-2 p-5 cursor-pointer hover:shadow-md transition-all duration-200 ${selectedCourse?.id === course.id ? 'border-primary' : 'border-border hover:border-primary/50'}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span className="bg-secondary/10 text-secondary text-xs font-bold px-2 py-0.5 rounded-full">{course.level}</span>
                        <span className="bg-muted text-muted-foreground text-xs px-2 py-0.5 rounded-full">{course.stream}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${demandColors[course.demand]}`}>
                          {course.demand} Demand
                        </span>
                      </div>
                      <h3 className="font-bold text-foreground">{course.name}</h3>
                    </div>
                    <ChevronRight size={18} className={`shrink-0 mt-1 transition-colors ${selectedCourse?.id === course.id ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{course.description}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock size={13} className="text-primary shrink-0" />
                      <span>{course.duration}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <BookOpen size={13} className="text-primary shrink-0" />
                      <span>{course.topExam}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <TrendingUp size={13} className="text-primary shrink-0" />
                      <span>{course.avgFees}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Briefcase size={13} className="text-primary shrink-0" />
                      <span>{course.avgSalary}</span>
                    </div>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="text-center py-16 bg-card rounded-2xl border border-border">
                  <BookOpen size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="font-bold text-foreground">No courses found</p>
                  <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters</p>
                </div>
              )}
            </div>

            {/* Course detail panel */}
            {selectedCourse && (
              <div className="lg:w-96 shrink-0">
                <div className="bg-card rounded-2xl border border-border overflow-hidden sticky top-24">
                  <div className="bg-secondary px-5 py-4">
                    <span className="text-xs text-primary font-bold uppercase tracking-wider">{selectedCourse.stream} · {selectedCourse.level}</span>
                    <h3 className="text-white font-bold text-lg mt-1 leading-tight">{selectedCourse.name}</h3>
                  </div>
                  <div className="p-5 space-y-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">{selectedCourse.description}</p>

                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Duration', value: selectedCourse.duration, icon: <Clock size={14} /> },
                        { label: 'Entrance Exam', value: selectedCourse.topExam, icon: <BookOpen size={14} /> },
                        { label: 'Avg Fees', value: selectedCourse.avgFees, icon: <TrendingUp size={14} /> },
                        { label: 'Avg Salary', value: selectedCourse.avgSalary, icon: <Briefcase size={14} /> },
                      ].map((item) => (
                        <div key={item.label} className="bg-muted rounded-xl p-3">
                          <div className="flex items-center gap-1.5 text-primary mb-1">{item.icon}<span className="text-xs text-muted-foreground">{item.label}</span></div>
                          <p className="font-bold text-sm text-foreground">{item.value}</p>
                        </div>
                      ))}
                    </div>

                    <div>
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Key Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedCourse.skills.map((skill) => (
                          <span key={skill} className="bg-accent text-accent-foreground text-xs font-semibold px-2.5 py-1 rounded-full border border-orange-200">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Top Colleges</p>
                      <div className="space-y-1.5">
                        {selectedCourse.topColleges.map((college, i) => (
                          <div key={college} className="flex items-center gap-2 text-sm">
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i === 0 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>{i + 1}</span>
                            <span className="text-foreground font-medium">{college}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Link href="/" className="btn-primary flex-1 text-sm">Find Colleges</Link>
                      <Link href="/college-predictor" className="btn-secondary flex-1 text-sm">Predict Chances</Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
